<template>
  <PaginaAdministrar
    titulo="Tipos de Produto"
    subtitulo="Classificação e exigências dinâmicas"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-5">
          <label class="form-label">Buscar por descrição</label>
          <input class="form-control" placeholder="Ex: Revenda..." v-model="filtros.busca" @keyup.enter="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatus" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="s in listas.status" :key="s.idListaSistemaItem" :value="s.idListaSistemaItem">{{ s.textoLista }}</option>
          </select>
        </div>
        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">
      <i class="bi bi-exclamation-triangle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="tiposProduto"
      :loading="loading"
      empty-message="Nenhum tipo de produto encontrado."
      row-key="idTipoProduto"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="emit('editar-item', $event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idTipoProduto }}</span>
      </template>
      <template #natureza="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle fw-normal">
          {{ getTextoNatureza(row.lstNaturezaItem) }}
        </span>
      </template>

      <template #destinacaoUso="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle fw-normal">
          {{ getDestinacaoUso(row) }}
        </span>
      </template>

      <template #compravel="{ row }">
        <i v-if="row.compravel" class="bi bi-check-circle-fill text-success"></i>
        <i v-else class="bi bi-x-circle text-muted"></i>
      </template>

      <template #controlaEstoque="{ row }">
        <i v-if="row.controlaEstoque" class="bi bi-check-circle-fill text-success"></i>
        <i v-else class="bi bi-x-circle text-muted"></i>
      </template>

      <template #controlaLote="{ row }">
        <i v-if="row.controlaLote" class="bi bi-check-circle-fill text-success"></i>
        <i v-else class="bi bi-x-circle text-muted"></i>
      </template>

      <template #conversaoVariavel="{ row }">
        <i v-if="row.permiteConversaoVariavel" class="bi bi-check-circle-fill text-success"></i>
        <i v-else class="bi bi-x-circle text-muted"></i>
      </template>

      <template #gerirEstoqueLogistica="{ row }">
        <i v-if="row.gerirEstoquePorUnidadeLogistica" class="bi bi-check-circle-fill text-success"></i>
        <i v-else class="bi bi-x-circle text-muted"></i>
      </template>

      <template #status="{ row }">
        <span class="badge" :class="obterClasseStatusGhost(getStatusTexto(row.lstStatus))">
          {{ getStatusTexto(row.lstStatus) }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper">
          <button class="btn-icon" title="Ver Histórico de Alterações" @click="abrirAuditoria(row)">
            <i class="bi bi-clock-history text-secondary"></i>
          </button>
          <button class="btn-icon text-primary" @click="emit('editar-item', row)" title="Editar">
            <i class="bi bi-pencil-fill"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="TipoProduto"
    :chave-primaria="auditoriaSelecionada.id"
    :tabelas-filhas="['TipoProdutoLista']"
    chave-estrangeira="IdTipoProduto"
    :titulo-registro="auditoriaSelecionada.nome"
    :dicionarios="{
      'lstStatus': listas.status,
      'status': listas.status
    }"
    @fechar="mostrarAuditoria = false"
  />
</template>

<script setup>
import { ref, reactive, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item'])

const authStore = useAuthStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)
const { obterClasseStatusGhost } = useUI()

const tiposProduto = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const metadataStore = useMetadataListasStore()

const listas = reactive({
  status: [],
  LstNaturezaItem: []
})

const filtros = reactive({
  busca: '',
  lstStatus: ''
})

const colunas = [
  { field: 'idTipoProduto', label: 'ID', width: 80, sortable: true, slotName: 'id' },
  { field: 'descricaoBreve', label: 'Descrição Breve', sortable: true, class: 'fw-medium text-body-emphasis' },
  { field: 'lstNaturezaItem', label: 'Natureza', align: 'center', sortable: true, slotName: 'natureza' },
  { field: 'destinacaoUso', label: 'Destinação de Uso', align: 'center', sortable: false, slotName: 'destinacaoUso' },
  { field: 'compravel', label: 'Comprável', align: 'center', sortable: true, slotName: 'compravel' },
  { field: 'controlaEstoque', label: 'Controla Estoque', align: 'center', sortable: true, slotName: 'controlaEstoque' },
  { field: 'controlaLote', label: 'Controla Lote', align: 'center', sortable: true, slotName: 'controlaLote' },
  { field: 'permiteConversaoVariavel', label: 'Conv. Variável', align: 'center', sortable: true, slotName: 'conversaoVariavel' },
  { field: 'gerirEstoquePorUnidadeLogistica', label: 'Ger. Estoque Logística', align: 'center', sortable: true, slotName: 'gerirEstoqueLogistica' },
  { field: 'lstStatus', label: 'Status', width: 120, align: 'center', sortable: true, slotName: 'status' },
  { field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstNaturezaItem: (row) => getTextoNatureza(row.lstNaturezaItem),
    lstStatus: (row) => getStatusTexto(row.lstStatus)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => {
  buscarDados(1)
}

const carregarListas = async () => {
  try {
    const [resStatus, metadataResult] = await Promise.all([
      api.get('/ListasSistema/lookup/ST'),
      metadataStore.carregarListasParaTabelas(['TipoProduto'])
    ])
    listas.status = resStatus.data
    Object.assign(listas, metadataResult)
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const getStatusTexto = (idStatus) => {
  if (!idStatus) return '-'
  const obj = listas.status.find(s => s.idListaSistemaItem === idStatus || s.idListaClienteItem === idStatus)
  return obj ? obj.textoLista : idStatus
}

const getTextoNatureza = (codigo) => {
  if (!codigo) return '-'
  const item = (listas.LstNaturezaItem || []).find(i => String(i.idListaClienteItem) === String(codigo))
  return item ? item.textoLista : codigo
}

const getDestinacaoUso = (row) => {
  if (row.comercializavel) return 'Comercialização/Revenda'
  if (row.industrializacao) return 'Industrialização'
  if (row.consumo) return 'Consumo'
  return '-'
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    tiposProduto.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/TiposProduto?${params.toString()}`)
    tiposProduto.value = response.data

    if (response.headers && response.headers['x-pagination']) {
      atualizarPaginacao(response.headers)
    }
  } catch (error) {
    console.error('Erro ao buscar Tipos de Produto:', error)
    erro.value = 'Não foi possível carregar os tipos de produto.'
    tiposProduto.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => { filtros.busca = ''; filtros.lstStatus = ''; buscarDados(1) }

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idTipoProduto
  auditoriaSelecionada.nome = item.descricaoBreve || `Tipo de Produto (ID: ${item.idTipoProduto})`
  mostrarAuditoria.value = true
}

watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) buscarDados(1)
  else { tiposProduto.value = []; paginacao.value.TotalCount = 0 }
})

onMounted(async () => {
  await carregarListas()
  if (authStore.empresaAtiva || !authStore.isAdminInterno) buscarDados(1)
})

onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

defineExpose({ buscarDados })
</script>

<style scoped>
</style>
